import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import * as _ from 'underscore';
import { BasePaylineEditComponent } from '../base-payline-edit/base-payline-edit.component';
import {
  CompanyEarningConfig,
  EarningCalculations
} from '../../../../models/company-earning-config';
import { Earning, Payline } from '../../../../models/payroll';
import { PayrollManagement } from '../../../../tools/payroll-management';
import { LaborAllocationService } from '../../../../services/labor-allocation.service';

@Component({
  selector: 'payline-earning-row',
  templateUrl: './payline-earning-row.component.html',
  styleUrls: ['./payline-earning-row.component.css'],
  standalone: false
})
export class PaylineEarningRowComponent extends BasePaylineEditComponent implements OnInit {
  @Input() config: CompanyEarningConfig;
  @Input() payline: Payline;
  @Input() earning: Earning;
  @Input() rowIndex: number;
  @Input() lastRowIndex: number;
  @Input() clearEarningsEvent: Observable<void>;
  @Input() updateEarningsEvent: Observable<void>;
  @Output() onEdit: EventEmitter<Earning> = new EventEmitter<Earning>();

  public rand: number;

  // form group to manage our user inputs
  public earningForm = new UntypedFormGroup({
    rateAmount: new UntypedFormControl(''),
    hours: new UntypedFormControl(''),
    total: new UntypedFormControl({ value: '', disabled: true })
  });

  get earningCalculations() {
    return EarningCalculations;
  }

  constructor(
    private laborAllocService: LaborAllocationService,
    private payrollMgmt: PayrollManagement
  ) {
    super();
    this._onHoursChange = _.debounce(this._onHoursChange, 500);
    this._onRateAmountChange = _.debounce(this._onRateAmountChange, 500);
  }

  ngOnInit(): void {
    this.updateEarningsEvent.subscribe(() => {
      this._setEarningValues(this.earning);
    });

    this.clearEarningsEvent.subscribe(() => {
      this.earning.TemporaryRateAmount = 0;
      this.earning.Hours = 0;
      this.earning.CurrentAmount = 0; // so total field updates immediately

      this._setEarningValues(this.earning);
    });

    if (this._allocatedByEarning()) {
      this._initEarningAllocationValues();
    } else {
      this._setEarningValues(this.earning);
    }

    this._configureControls(this.config);
  }

  // NOTE: object reference is broken from the payroll data model.
  // when changes to the current amount are detected,
  // (from Preview Totals), update the total field
  ngDoCheck() {
    // NOTE: when blank, this.earningForm.get('total') is an empty string
    let value = this.earningForm.get('total').value || null;
    // CurrentAmount can be null or 0
    let currentAmount = this.earning.CurrentAmount || null;
    if (currentAmount != value) {
      this.earningForm.get('total').setValue(this.earning.CurrentAmount);
    }

    if (this._allocatedByEarning()) {
      this._initEarningAllocationValues();
    } else if (this.config.SourceEarning) {
      // the "Use Rate From" earning rates that depend on other earnings
      // need their values updated as well
      let rate = this.earningForm.get('rateAmount').value || null;
      let currentRate =
        this.earning.TemporaryRateAmount || this.earning.PermanentRateAmount || null;

      if (rate != currentRate) {
        this.earningForm
          .get('rateAmount')
          .setValue(this.earning.TemporaryRateAmount || this.earning.PermanentRateAmount);
      }
    }
  }

  // #region Public Methods

  // listen for changes to our inputs.  when we catch a change, build and emit an
  // updated Earning model through our @Output emitter
  public onHoursChange(evnt): void {
    // attempt to let users to keyboard navigate in the detail editor
    if (this.navigationKeys.includes(evnt.code)) {
      this.onKeyboardNavigate(
        evnt.code,
        this.rowIndex,
        1, // hours column is at focusable column index 1
        this.lastRowIndex
      );
    }

    this._onHoursChange(evnt);
  }

  public onRateAmountChange(evnt): void {
    // attempt to let users to keyboard navigate in the detail editor
    if (this.navigationKeys.includes(evnt.code)) {
      this.onKeyboardNavigate(
        evnt.code,
        this.rowIndex,
        0, // rate/amount is at focusable column index 0
        this.lastRowIndex
      );
    }

    this._onRateAmountChange(evnt);
  }

  public updateEarningValues() {
    this._setEarningValues(this.earning);
  }

  // #endregion

  // #region Private Methods

  private _allocatedByEarning() {
    return Object.values(this.payline.Allocations ?? {}).some(
      alloc =>
        alloc.TemporaryHourlyAllocations &&
        Object.keys(alloc.TemporaryHourlyAllocations[this.earning.EarningId] ?? {}).length
    );
  }

  // onConfigChanged: update the display name and toggle dis/enabled flags
  private _configureControls(config: CompanyEarningConfig): void {
    // task 18212
    if (!this.payrollMgmt.isEarningEditable(this.payline, config.Id)) {
      this.earningForm.get('rateAmount').disable();
      this.earningForm.get('hours').disable();
      return;
    }

    // bug 21735 disable the rate field specifically for UseRateFrom calculation methods

    if (config.CalculationMethod == this.earningCalculations['Use Rate From']) {
      this.earningForm.get('rateAmount').disable();
    } else {
      this.earningForm.get('rateAmount').enable();
    }

    if (config.CanSetHours) {
      this.earningForm.get('hours').enable();
    } else {
      this.earningForm.get('hours').disable();
    }
  }

  private _initEarningAllocationValues() {
    // US 19922
    const hours = this.laborAllocService.CalculateTotalHours(this.earning, this.payline);
    const amount = this.laborAllocService.CalculateTotalRate(this.earning, this.payline);

    if (hours) {
      this.earningForm.get('hours').setValue(hours, { emitValue: false });
    }

    if (this.config.IsPercentageEarning) {
      this.earningForm.get('rateAmount').setValue('', { emitValue: false });
    } else {
      this.earningForm.get('rateAmount').setValue(amount, { emitValue: false });
    }
  }

  private _onHoursChange(evnt): void {
    // NOTE: if value is blank, pass 0 to the API
    // to ensure totals are returned properly
    let value = evnt?.target?.value || 0;
    this.earning.Hours = value;
    this.onEdit.emit(this.earning);
  }

  private _onRateAmountChange(evnt): void {
    // NOTE: if value is blank, pass 0 to the API
    // to ensure totals are returned properly
    let value = evnt?.target?.value || 0;
    this.earning.TemporaryRateAmount = value;
    this.onEdit.emit(this.earning);
  }

  // update the values for our displayed inputs
  private _setEarningValues(earning: Earning): void {
    this.earningForm
      .get('rateAmount')
      // NOTE: use ?? to compare so temporary rates of 0 are taken
      .setValue(earning.TemporaryRateAmount ?? earning.PermanentRateAmount);

    this.earningForm.get('hours').setValue(earning.Hours);

    this.earningForm.get('total').setValue(earning.CurrentAmount);
  }
}
